
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export function SocialLoginOptions() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [redirectUrl, setRedirectUrl] = useState<string>("");

  useEffect(() => {
    // Get the current origin (protocol + hostname + port)
    const origin = window.location.origin;
    const redirectPath = `${origin}/auth`;
    console.log("Setting redirect URL to:", redirectPath);
    setRedirectUrl(redirectPath);
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      console.log("Starting Google OAuth sign-in process");
      console.log("Using redirect URL:", redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          redirectTo: redirectUrl
        }
      });
      
      if (error) throw error;
      console.log("Google OAuth initiated successfully", data);
      
      // When successful, we'll be redirected away, but if something goes wrong
      // and we're still here, we should stop the loading indicator
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);
      
    } catch (error: any) {
      console.error("Google Sign In Error:", error);
      setErrorMessage(error.message);
      toast({
        title: "Google Sign In Error",
        description: error.message,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("mt-6", isMobile && "px-2")}>
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {errorMessage}
            <div className="mt-2 text-xs">
              Please make sure your Google OAuth credentials are properly configured in Supabase.
              Also verify that your OAuth consent screen shows your application name.
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-4">
        <Button 
          variant="outline" 
          className="w-full flex items-center justify-center mobile-touch-target"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          size={isMobile ? "lg" : "default"}
        >
          <GoogleLogo className="mr-2 h-5 w-5" />
          <span className={cn("text-sm", isMobile && "text-base")}>
            {isLoading ? "Connecting..." : "Continue with Google"}
          </span>
        </Button>
      </div>
    </div>
  );
}

function GoogleLogo(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="18" 
      height="18" 
      viewBox="0 0 18 18"
      {...props}
    >
      <g fill="none" fillRule="evenodd">
        <path 
          fill="#4285F4" 
          d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
        />
        <path 
          fill="#34A853" 
          d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
        />
        <path 
          fill="#FBBC05" 
          d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
        />
        <path 
          fill="#EA4335" 
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
        />
      </g>
    </svg>
  );
}
